/**
 * THE MENU/BATTLE MUSIC MANAGER — one music bed for the whole app outside the
 * match's own adaptive director.
 *
 * ── WHAT IT OWNS AND WHAT IT DELIBERATELY DOES NOT ──────────────────────────
 * This plays two long loops through plain `HTMLAudioElement`s: `menu-loop.mp3`
 * while the meta shell is up and `battle-loop.mp3` during a match, cross-fading
 * between them. It is NOT the match's `engine/match/audio` — that module owns
 * one-shot effects and a three-stem adaptive score built from WebAudio buffers,
 * and it stays untouched. Two music systems would fight, so this one steps
 * ASIDE for a match: `setScene('battle')` here is meant for a match that does
 * not run its own director, and a caller that does run one calls
 * `setScene('none')` instead. The manager never assumes; the caller decides.
 *
 * Streamed `<audio>` rather than decoded WebAudio buffers on purpose: a two- or
 * three-minute loop is a large decode to hold resident just to play it at 0.35
 * gain behind a menu, and the browser streams and loops an `<audio>` element
 * for free. The trade is that its only volume control is the linear `.volume`
 * property — no sample-accurate ramp — so the cross-fade is stepped on a frame
 * timer here. That is audible-clean for a slow bed fade; it would not be for
 * the match's beat-locked stem fades, which is the other reason those live
 * elsewhere.
 *
 * ── THE AUTOPLAY LOCK (get this wrong and it is silent on load, or worse, a
 *    browser-console error on every visit) ──────────────────────────────────
 * Every browser blocks audio that starts outside a user gesture. So the manager
 * starts LOCKED: `setScene` records the desired scene and plays nothing until
 * `unlock()` is awaited from inside a real `pointerdown`/`keydown`/`touchend`.
 * `attachUnlockGestures(target)` wires that and detaches on the first success.
 * `.play()` can still reject (a policy we did not foresee, no output device);
 * every call swallows it — a bed that will not start is silence, never a throw.
 *
 * ── A MISSING FILE IS SILENCE, NOT A CRASH ──────────────────────────────────
 * Phase-2 has no licensed audio assets; the owner drops the mp3s in later.
 * Until then `menu-loop.mp3` 404s (or, under the dev server, comes back as
 * `index.html` with a 200, which is the same thing to a decoder). The element
 * fires `error` and the manager moves to the NEXT candidate for that scene —
 * see `MUSIC_SOURCES` — and when the candidates run out it marks the track dead
 * and never retries it. Everything else keeps working: no assertion, no console
 * spew, no unhandled rejection.
 *
 * ── LIVE PREFS ──────────────────────────────────────────────────────────────
 * The element volume is always `master * music * (muted ? 0 : 1) * fade`. The
 * manager subscribes to `audioPrefsStore`, so dragging the Music slider or
 * hitting Mute in Settings moves the bed on the same frame, with no reload and
 * no replumbing from the settings screen.
 */
import { audioPrefsStore, initAudioPrefs, type AudioPrefs } from './prefs';

/* ═══════════════════════════════════════════════════════════════ types ══ */

export type MusicScene = 'none' | 'menu' | 'battle';

/** The two tracks. Paths the orchestrator fills; referenced, never generated. */
export const MUSIC_TRACKS: Readonly<Record<Exclude<MusicScene, 'none'>, string>> = Object.freeze({
  menu: '/audio/menu-loop.mp3',
  battle: '/audio/battle-loop.mp3',
});

/**
 * THE CANDIDATE LIST PER SCENE, TRIED IN ORDER.
 *
 * `MUSIC_TRACKS` above is the LICENSED bed: two long loops the owner drops into
 * `frontend/public/audio/`. Neither file exists in this repo and neither will be
 * invented here. Until they do, the first candidate fails and the manager falls
 * through to the SECOND — an arena music stem the asset pipeline generates
 * itself (`tools/assetgen` → `/assets/audio/music/<arena>/calm.wav`, an 8-second
 * seamless loop, already shipping). That is a real bed made of a real file this
 * build already serves, not a placeholder tone.
 *
 * DELETE THE SECOND ENTRY, or simply drop the mp3s in, and the licensed music
 * takes over with no other change. The order is the whole policy.
 *
 * The default inside `AudioManager` is still the single `MUSIC_TRACKS` entry —
 * a manager constructed with explicit deps (every test) keeps the original
 * "one file, and a failure is silence" behaviour.
 */
export const MUSIC_SOURCES: Readonly<Record<Exclude<MusicScene, 'none'>, readonly string[]>> =
  Object.freeze({
    menu: Object.freeze([MUSIC_TRACKS.menu, '/assets/audio/music/cinder-flats/calm.wav']),
    battle: Object.freeze([MUSIC_TRACKS.battle, '/assets/audio/music/cinder-flats/tension.wav']),
  });

/** The exact subset of `HTMLAudioElement` the manager touches. */
export interface MediaElementLike {
  src: string;
  loop: boolean;
  volume: number;
  currentTime: number;
  play(): Promise<void> | void;
  pause(): void;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface UnlockTargetLike {
  addEventListener(type: string, listener: () => void, options?: unknown): void;
  removeEventListener(type: string, listener: () => void, options?: unknown): void;
}

export interface AudioManagerDeps {
  /** Build a media element for a URL, or null where `<audio>` is absent (SSR). */
  createMedia?(src: string): MediaElementLike | null;
  /** Monotonic ms clock. Defaults to `performance.now()`. */
  now?(): number;
  /** Schedule one frame; returns a cancel. Defaults to rAF, then setTimeout. */
  requestFrame?(cb: () => void): () => void;
  /** Cross-fade length in ms. A slow bed fade; not beat-locked. */
  crossfadeMs?: number;
  /**
   * Candidate URLs per scene, tried in order until one plays. Defaults to the
   * single `MUSIC_TRACKS` entry, which is the historical behaviour: one file,
   * and a failure is silence.
   */
  sources?: Readonly<Record<Exclude<MusicScene, 'none'>, readonly string[]>>;
  /** Inject the prefs source. Defaults to the shared store. */
  prefs?(): AudioPrefs;
  /** Subscribe to prefs; returns an unsubscribe. Defaults to the shared store. */
  subscribePrefs?(cb: (p: AudioPrefs) => void): () => void;
}

/* ═══════════════════════════════════════════════════════════ defaults ══ */

const UNLOCK_EVENTS: readonly string[] = Object.freeze([
  'pointerdown',
  'pointerup',
  'touchend',
  'keydown',
  'click',
]);

const DEFAULT_CROSSFADE_MS = 900;

function nowDefault(): number {
  try {
    return performance.now();
  } catch {
    return Date.now();
  }
}

function requestFrameDefault(cb: () => void): () => void {
  const g = globalThis as unknown as {
    requestAnimationFrame?: (cb: () => void) => number;
    cancelAnimationFrame?: (h: number) => void;
  };
  if (typeof g.requestAnimationFrame === 'function') {
    const h = g.requestAnimationFrame(cb);
    return () => g.cancelAnimationFrame?.(h);
  }
  const h = setTimeout(cb, 16);
  return () => clearTimeout(h);
}

function createMediaDefault(src: string): MediaElementLike | null {
  const g = globalThis as unknown as { Audio?: new (src?: string) => MediaElementLike };
  if (typeof g.Audio !== 'function') return null;
  try {
    const a = new g.Audio();
    a.src = src;
    a.loop = true;
    a.volume = 0;
    return a;
  } catch {
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════ a track ══ */

interface Track {
  readonly scene: Exclude<MusicScene, 'none'>;
  /** URLs to try, in order. */
  readonly sources: readonly string[];
  /** Which of `sources` the current element holds. */
  sourceIndex: number;
  media: MediaElementLike | null;
  /** Cross-fade gain 0..1. Ramped by `tick`. */
  fade: number;
  /** Where `fade` is heading. */
  fadeTarget: number;
  /** Whether the element is currently meant to be playing. */
  wantPlay: boolean;
  /** The file 404'd or failed. Never retried; kept silent. */
  dead: boolean;
  detachError: (() => void) | null;
}

/* ═══════════════════════════════════════════════════════════ manager ══ */

export class AudioManager {
  private readonly makeMedia: (src: string) => MediaElementLike | null;
  private readonly clockMs: () => number;
  private readonly requestFrame: (cb: () => void) => () => void;
  private readonly readPrefs: () => AudioPrefs;
  private readonly crossfadeMs: number;

  private readonly tracks: Track[] = [];
  private scene: MusicScene = 'none';
  private unlocked = false;
  private destroyed = false;

  private cancelFrame: (() => void) | null = null;
  private lastTickMs = 0;
  private detachUnlock: (() => void) | null = null;
  private unsubscribePrefs: (() => void) | null = null;

  constructor(deps: AudioManagerDeps = {}) {
    this.makeMedia = deps.createMedia ?? createMediaDefault;
    this.clockMs = deps.now ?? nowDefault;
    this.requestFrame = deps.requestFrame ?? requestFrameDefault;
    this.crossfadeMs = deps.crossfadeMs !== undefined && deps.crossfadeMs > 0 ? deps.crossfadeMs : DEFAULT_CROSSFADE_MS;
    this.readPrefs = deps.prefs ?? (() => audioPrefsStore.get());
    // Load persisted volumes before the bed can start. No-op under an injected
    // prefs source (a test), which owns its own values.
    if (deps.prefs === undefined) initAudioPrefs();

    const sources = deps.sources;
    for (const scene of ['menu', 'battle'] as const) {
      const list = sources?.[scene];
      this.tracks.push({
        scene,
        sources: list !== undefined && list.length > 0 ? list : [MUSIC_TRACKS[scene]],
        sourceIndex: 0,
        media: null,
        fade: 0,
        fadeTarget: 0,
        wantPlay: false,
        dead: false,
        detachError: null,
      });
    }

    const sub = deps.subscribePrefs ?? ((cb) => audioPrefsStore.subscribe(cb));
    // Fires immediately with the current prefs, so this is also the first
    // volume application. Any later slider/mute change lands here too.
    this.unsubscribePrefs = sub(() => this.applyVolumes());
  }

  /* --------------------------------------------------------------- state */

  get currentScene(): MusicScene {
    return this.scene;
  }

  get isUnlocked(): boolean {
    return this.unlocked && !this.destroyed;
  }

  get isDestroyed(): boolean {
    return this.destroyed;
  }

  /** The live fade gain for a scene's track. For tests and a debug overlay. */
  fadeOf(scene: Exclude<MusicScene, 'none'>): number {
    return this.trackFor(scene)?.fade ?? 0;
  }

  /** The element volume actually written to a scene's track. */
  volumeOf(scene: Exclude<MusicScene, 'none'>): number {
    return this.trackFor(scene)?.media?.volume ?? 0;
  }

  /** Was a track's file marked dead (missing / failed)? */
  isDead(scene: Exclude<MusicScene, 'none'>): boolean {
    return this.trackFor(scene)?.dead ?? false;
  }

  /* --------------------------------------------------------------- scene */

  /**
   * Ask for a scene. Records it even while locked; the cross-fade only starts
   * once `unlock()` has run. Idempotent — asking for the current scene is a
   * no-op that does not restart the loop.
   */
  setScene(next: MusicScene): void {
    if (this.destroyed || next === this.scene) return;
    this.scene = next;
    for (const track of this.tracks) {
      const wanted = track.scene === next;
      track.fadeTarget = wanted ? 1 : 0;
      if (wanted && this.unlocked) this.ensurePlaying(track);
    }
    if (this.unlocked) this.startTicking();
  }

  /* -------------------------------------------------------------- unlock */

  /** Start audio. MUST be awaited from inside a real user gesture. */
  async unlock(): Promise<boolean> {
    if (this.destroyed) return false;
    if (this.unlocked) return true;
    this.unlocked = true;
    // Bring the target scene's track to life; the fade timer takes it from 0.
    for (const track of this.tracks) {
      if (track.fadeTarget > 0) this.ensurePlaying(track);
    }
    this.startTicking();
    return true;
  }

  /**
   * Wire the first user gesture on `target` to `unlock()`. Idempotent; returns
   * a detach handle. The listeners remove themselves once unlocked.
   */
  attachUnlockGestures(target: UnlockTargetLike): () => void {
    if (this.destroyed) return () => undefined;
    if (this.detachUnlock !== null) return this.detachUnlock;
    const handler = (): void => {
      void this.unlock().then((ok) => {
        if (ok) detach();
      });
    };
    const detach = (): void => {
      for (const ev of UNLOCK_EVENTS) target.removeEventListener(ev, handler);
      if (this.detachUnlock === detach) this.detachUnlock = null;
    };
    for (const ev of UNLOCK_EVENTS) {
      target.addEventListener(ev, handler, { once: false, passive: true });
    }
    this.detachUnlock = detach;
    return detach;
  }

  /* ------------------------------------------------------------- volumes */

  /**
   * Re-apply the pref-derived volume to every track WITHOUT touching the fade.
   * Called on every prefs change and after every fade tick.
   */
  applyVolumes(): void {
    for (const track of this.tracks) this.writeVolume(track);
  }

  private writeVolume(track: Track): void {
    if (track.media === null) return;
    const p = this.readPrefs();
    const base = p.muted ? 0 : clamp01(p.master) * clamp01(p.music);
    track.media.volume = clamp01(base * track.fade);
  }

  /* ---------------------------------------------------------------- fade */

  private startTicking(): void {
    if (this.cancelFrame !== null || this.destroyed) return;
    this.lastTickMs = this.clockMs();
    const loop = (): void => {
      this.cancelFrame = null;
      if (this.destroyed) return;
      const running = this.tick();
      if (running) this.cancelFrame = this.requestFrame(loop);
    };
    this.cancelFrame = this.requestFrame(loop);
  }

  /**
   * Advance every fade toward its target by the elapsed slice. Returns true
   * while any fade is still moving, so the timer stops itself at rest rather
   * than spinning a frame loop forever behind a static menu. Exposed for tests
   * that drive the clock by hand.
   */
  tick(): boolean {
    const now = this.clockMs();
    const dt = Math.max(0, now - this.lastTickMs);
    this.lastTickMs = now;
    const step = this.crossfadeMs > 0 ? dt / this.crossfadeMs : 1;
    let moving = false;
    for (const track of this.tracks) {
      if (track.fade !== track.fadeTarget) {
        const dir = track.fadeTarget > track.fade ? 1 : -1;
        track.fade = clamp01(track.fade + dir * step);
        if (track.fade === track.fadeTarget) {
          // Reached silence — release the element from playback so a hidden
          // menu track is not decoding for nothing.
          if (track.fadeTarget === 0) this.pause(track);
        } else {
          moving = true;
        }
      }
      this.writeVolume(track);
    }
    return moving;
  }

  /* --------------------------------------------------------- element I/O */

  private ensurePlaying(track: Track): void {
    if (track.dead) return;
    if (track.media === null) {
      const url = track.sources[track.sourceIndex];
      if (url === undefined) {
        track.dead = true;
        return;
      }
      const media = this.makeMedia(url);
      if (media === null) {
        // No `<audio>` at all (SSR, a locked-down runtime). No candidate can
        // help, so this is dead for good rather than a source to skip past.
        track.dead = true;
        return;
      }
      media.loop = true;
      media.volume = 0;
      /*
       * A 404, a decode failure, or — the trap this repo has documented before
       * — a dev server answering a missing asset with `index.html` AND A 200,
       * which reaches the element as undecodable media rather than as an HTTP
       * error. All three arrive here, and all three mean "this URL is not
       * music": drop the element and try the next candidate. When the list runs
       * out the track is dead and never retried. Never a throw, never a log.
       */
      const onError = (): void => {
        this.pause(track);
        track.detachError?.();
        track.detachError = null;
        track.media = null;
        track.sourceIndex++;
        if (track.sourceIndex >= track.sources.length) {
          track.dead = true;
          return;
        }
        // Only chase the next candidate while this scene is still wanted.
        if (track.fadeTarget > 0 && this.unlocked && !this.destroyed) this.ensurePlaying(track);
      };
      media.addEventListener('error', onError);
      track.detachError = () => media.removeEventListener('error', onError);
      track.media = media;
    }
    if (!track.wantPlay) {
      track.wantPlay = true;
      this.writeVolume(track);
      try {
        const r = track.media.play();
        // A rejected promise is a policy refusal, not our bug. Swallow it and
        // stay silent; the next gesture-driven `setScene` tries again.
        if (r !== undefined && typeof (r as Promise<void>).then === 'function') {
          (r as Promise<void>).then(undefined, () => {
            /* autoplay refused — silence, not a throw */
          });
        }
      } catch {
        /* play threw synchronously (old Safari) — stay silent */
      }
    }
  }

  private pause(track: Track): void {
    if (track.media === null || !track.wantPlay) return;
    track.wantPlay = false;
    try {
      track.media.pause();
    } catch {
      /* already paused */
    }
  }

  private trackFor(scene: Exclude<MusicScene, 'none'>): Track | undefined {
    return this.tracks.find((t) => t.scene === scene);
  }

  /* ------------------------------------------------------------- destroy */

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cancelFrame?.();
    this.cancelFrame = null;
    this.detachUnlock?.();
    this.unsubscribePrefs?.();
    this.unsubscribePrefs = null;
    for (const track of this.tracks) {
      this.pause(track);
      track.detachError?.();
      track.detachError = null;
      track.media = null;
    }
  }
}

function clamp01(v: number): number {
  return !(v > 0) ? 0 : v > 1 ? 1 : v;
}

/* ═══════════════════════════════════════════════════════════ singleton ══ */

let shared: AudioManager | null = null;

/**
 * The app-wide manager.
 *
 * MOUNTED IN `src/main.ts` (`mountShellAudio`): it attaches the unlock gestures
 * to `window` once and calls `setScene('menu')` on every route except `/arena`,
 * which gets `setScene('none')` because the match runs its own adaptive score
 * (`engine/match/audio/music.ts`) and two music systems would fight. Those were
 * the mount lines this file's header asked for and nothing had written.
 */
export function audioManager(): AudioManager {
  if (shared === null || shared.isDestroyed) shared = new AudioManager({ sources: MUSIC_SOURCES });
  return shared;
}

/** Test seam: drop the singleton so the next `audioManager()` is fresh. */
export function resetAudioManagerForTests(): void {
  shared?.destroy();
  shared = null;
}
